import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Card, 
  Typography, 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Select, 
  message, 
  Popconfirm, 
  Row, 
  Col,
  Tag,
  // Upload, // 未使用，已移除
  Divider,
  // Statistic, // 未使用，已移除
  Tooltip,
  Drawer,
  Descriptions,
  Grid,
  Dropdown,
  // InputNumber, // 未使用，已移除
  Pagination,
  Badge
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  UploadOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  DownloadOutlined,
  CarOutlined,
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

interface Vehicle {
  id?: number;
  plate_number: string;
  driver: string;
  phone: string;
  remark: string;
}

interface Carrier {
  id?: number;
  name: string;
  manager: string;
  address: string;
  phone: string;
  id_card: string;
  remark: string;
  status: string;
  vehicles: Vehicle[];
  created_at?: string;
  updated_at?: string;
}

interface ImportResult {
  success: number;
  skipped: number;
  failed: number;
  errors: Array<{ row: number; reason: string }>;
}

const CarrierManagement: React.FC = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  
  const [data, setData] = useState<Carrier[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingCarrier, setEditingCarrier] = useState<Carrier | null>(null);
  const [selectedCarrier, setSelectedCarrier] = useState<Carrier | null>(null);
  const [form] = Form.useForm();
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [importData, setImportData] = useState<any[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [searchText, setSearchText] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  // 获取承运单位列表
  const fetchData = useCallback(async (params: Record<string, string> = {}) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        current: current.toString(),
        pageSize: pageSize.toString(),
        ...params
      });
      
      const response = await fetch(`/api/carriers?${queryParams}`);
      const result = await response.json();
      
      if (result.code === 0) {
        setData(result.data);
        setTotal(result.total);
      } else {
        messageApi.error({
          content: '获取承运单位数据失败，请刷新页面重试',
          duration: 4,
        });
      }
    } catch (error) {
      messageApi.error({
        content: '网络错误，无法获取承运单位数据，请检查网络连接',
        duration: 4,
      });
    } finally {
      setLoading(false);
    }
  }, [current, pageSize, messageApi]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 分页处理
  const handlePageChange = useCallback((page: number, size: number) => {
    setCurrent(page);
    setPageSize(size);
  }, []);

  // 搜索处理
  const handleSearch = useCallback((searchValue?: string) => {
    const searchTextToUse = searchValue !== undefined ? searchValue : searchText;
    if (searchTextToUse.trim()) {
      const params = { search: searchTextToUse.trim() };
      setCurrent(1);
      fetchData(params);
    } else {
      // 如果搜索文本为空，则获取所有数据
      setCurrent(1);
      fetchData();
    }
  }, [fetchData, setCurrent, searchText]);

  // 重置搜索
  const handleReset = useCallback(() => {
    setSearchText('');
    setCurrent(1);
    fetchData();
  }, [fetchData]);

  // 提交表单
  const handleSubmit = useCallback(async (values: any) => {
    try {
      const url = editingCarrier 
        ? `/api/carriers/${editingCarrier.id}`
        : '/api/carriers';
      
      const method = editingCarrier ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values)
      });
      
      const result = await response.json();
      
      if (result.code === 0) {
        messageApi.success({
          content: editingCarrier ? '承运单位信息更新成功！' : '承运单位创建成功！',
          duration: 3,
        });
        setModalVisible(false);
        setEditingCarrier(null);
        form.resetFields();
        fetchData();
      } else {
        messageApi.error({
          content: result.message || '承运单位操作失败，请重试',
          duration: 4,
        });
      }
    } catch (error) {
      messageApi.error({
        content: '网络错误，操作失败，请检查网络连接后重试',
        duration: 4,
      });
    }
  }, [editingCarrier, form, fetchData, messageApi]);

  // 删除承运单位
  const handleDelete = useCallback(async (id: number) => {
    try {
      const response = await fetch(`/api/carriers/${id}`, {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (result.code === 0) {
        messageApi.success({
          content: '承运单位删除成功！',
          duration: 3,
        });
        fetchData();
      } else {
        messageApi.error({
          content: result.message || '承运单位删除失败，请重试',
          duration: 4,
        });
      }
    } catch (error) {
      messageApi.error({
        content: '网络错误，删除失败，请检查网络连接后重试',
        duration: 4,
      });
    }
  }, [fetchData, messageApi]);

  // 编辑承运单位
  const handleEdit = useCallback((record: Carrier) => {
    setEditingCarrier(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  }, [form]);

  // 查看承运单位详情
  const handleView = useCallback((carrier: Carrier) => {
    setSelectedCarrier(carrier);
    setDrawerVisible(true);
  }, []);

  // 新增承运单位
  const handleAdd = useCallback(() => {
    setEditingCarrier(null);
    form.resetFields();
    form.setFieldsValue({
      status: '开启',
      vehicles: []
    });
    setModalVisible(true);
  }, [form]);

  // 渲染车辆卡片
  const renderVehicleCard = useCallback((vehicle: Vehicle, index: number) => (
    <Card 
      key={index} 
      size="small" 
      style={{ marginBottom: 8 }}
      bodyStyle={{ padding: 8 }}
    >
      <Row gutter={8} align="middle">
        <Col span={8}>
          <Text strong>{vehicle.plate_number}</Text>
        </Col>
        <Col span={6}>
          <Text>{vehicle.driver}</Text>
        </Col>
        <Col span={6}>
          <Text>{vehicle.phone}</Text>
        </Col>
        <Col span={4}>
          <Text type="secondary">{vehicle.remark}</Text>
        </Col>
      </Row>
    </Card>
  ), []);

  // 移动端卡片渲染
  const renderCarrierCard = useCallback((carrier: Carrier) => (
    <Card 
      key={carrier.id} 
      size="small" 
      style={{ marginBottom: 12 }}
      actions={[
        <Tooltip title="查看详情" key="view">
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            size="small"
            onClick={() => handleView(carrier)}
          >
            详情
          </Button>
        </Tooltip>,
        <Tooltip title="编辑" key="edit">
          <Button 
            type="text" 
            icon={<EditOutlined />} 
            size="small"
            onClick={() => handleEdit(carrier)}
          >
            编辑
          </Button>
        </Tooltip>,
        <Popconfirm
          title="确定要删除这个承运单位吗？"
          onConfirm={() => handleDelete(carrier.id!)}
          okText="确定"
          cancelText="取消"
          key="delete"
        >
          <Tooltip title="删除">
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
            >
              删除
            </Button>
          </Tooltip>
        </Popconfirm>
      ]}
    >
      <Row gutter={[8, 6]}>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">承运单位：</Text>
            <div style={{ textAlign: 'right' }}>
              <Text strong style={{ fontSize: 16 }}>{carrier.name}</Text>
              <Tag color={carrier.status === '开启' ? 'green' : 'red'} style={{ marginLeft: 6 }}>
                {carrier.status}
              </Tag>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">负责人：</Text>
            <Text style={{ textAlign: 'right' }}>{carrier.manager || '未设置'}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">电话：</Text>
            <Text copyable style={{ textAlign: 'right' }}>{carrier.phone || '未设置'}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">地址：</Text>
            <Text style={{ textAlign: 'right' }}>{carrier.address || '未设置'}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">车辆数量：</Text>
            <div style={{ textAlign: 'right' }}>
              <Badge count={carrier.vehicles?.length || 0} showZero>
                <CarOutlined style={{ fontSize: 16 }} />
              </Badge>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Text type="secondary">备注：</Text>
            <div style={{ textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>
              <Text style={{ whiteSpace: 'pre-wrap' }}>{carrier.remark || '无'}</Text>
            </div>
          </div>
        </Col>
      </Row>
      
      {carrier.vehicles && carrier.vehicles.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Divider orientation="left">车辆信息</Divider>
          {carrier.vehicles.map((vehicle, index) => renderVehicleCard(vehicle, index))}
        </div>
      )}
    </Card>
  ), [renderVehicleCard, handleView, handleEdit, handleDelete]);

  // 文件上传处理
  const handleFileUpload = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n');
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        const data = lines.slice(1).filter(line => line.trim()).map((line, index) => {
          const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
          const row: any = {};
          headers.forEach((header, i) => {
            row[header] = values[i] || '';
          });
          row._rowIndex = index + 1;
          return row;
        });

        // 智能字段映射
        const defaultMapping: Record<string, string> = {};
        const usedTargetFields = new Set<string>();
        
        headers.forEach(header => {
          const headerLower = header.toLowerCase();
          let targetField = '';
          
          if (headerLower.includes('名称') || headerLower.includes('name')) {
            targetField = 'name';
          } else if (headerLower.includes('负责人') || headerLower.includes('manager')) {
            targetField = 'manager';
          } else if (headerLower.includes('地址') || headerLower.includes('address')) {
            targetField = 'address';
          } else if (headerLower.includes('电话') || headerLower.includes('phone')) {
            targetField = 'phone';
          } else if (headerLower.includes('身份证') || headerLower.includes('id_card')) {
            targetField = 'id_card';
          } else if (headerLower.includes('备注') || headerLower.includes('remark')) {
            targetField = 'remark';
          } else if (headerLower.includes('状态') || headerLower.includes('status')) {
            targetField = 'status';
          }
          
          if (targetField && usedTargetFields.has(targetField)) {
            targetField = '';
          }
          
          if (targetField) {
            defaultMapping[header] = targetField;
            usedTargetFields.add(targetField);
          } else {
            defaultMapping[header] = '';
          }
        });

        setFieldMapping(defaultMapping);
        setImportData(data);
        setImportModalVisible(true);
      } catch (error) {
        messageApi.error({
          content: '文件解析失败，请检查文件格式',
          duration: 4,
        });
      }
    };
    reader.readAsText(file);
    return false;
  }, [messageApi]);

  // 批量导入
  const handleImport = useCallback(async () => {
    try {
      const mappedData = importData.map(row => {
        const mapped: any = {};
        Object.keys(fieldMapping).forEach(sourceField => {
          const targetField = fieldMapping[sourceField];
          if (targetField && targetField !== '') {
            mapped[targetField] = row[sourceField];
          }
        });
        return mapped;
      });

      const response = await fetch('/api/carriers/batch-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: mappedData })
      });

      const result = await response.json();
      
      if (result.code === 0) {
        setImportResult(result.data);
        messageApi.success({
          content: '承运单位导入完成！',
          duration: 3,
        });
        fetchData();
      } else {
        messageApi.error({
          content: result.message || '承运单位导入失败，请重试',
          duration: 4,
        });
      }
    } catch (error) {
      messageApi.error({
        content: '网络错误，导入失败，请检查网络连接后重试',
        duration: 4,
      });
    }
  }, [importData, fieldMapping, fetchData, messageApi]);

  // 导出CSV
  const exportToCSV = useCallback(async () => {
    setExportLoading(true);
    try {
      const response = await fetch('/api/carriers?pageSize=10000');
      const result = await response.json();
      
      if (result.code !== 0) {
        throw new Error('获取导出数据失败');
      }
      
      const exportData = result.data.map((carrier: Carrier) => ({
        '承运单位': carrier.name,
        '负责人': carrier.manager,
        '地址': carrier.address,
        '电话': carrier.phone,
        '身份证号': carrier.id_card,
        '备注': carrier.remark,
        '状态': carrier.status,
        '车辆数量': (carrier.vehicles || []).length
      }));

      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map((row: Record<string, any>) => 
          headers.map(header => `"${row[header] || ''}"`).join(',')
        )
      ].join('\n');

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `承运单位_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      messageApi.success({
        content: 'CSV文件导出成功！',
        duration: 3,
      });
    } catch (error) {
      messageApi.error({
        content: 'CSV文件导出失败，请重试',
        duration: 4,
      });
    } finally {
      setExportLoading(false);
    }
  }, [messageApi]);

  // 导出Excel
  const exportToExcel = useCallback(async () => {
    setExportLoading(true);
    try {
      const response = await fetch('/api/carriers?pageSize=10000');
      const result = await response.json();
      
      if (result.code !== 0) {
        throw new Error('获取导出数据失败');
      }
      
      const exportData = result.data.map((carrier: Carrier) => ({
        '承运单位': carrier.name,
        '负责人': carrier.manager,
        '地址': carrier.address,
        '电话': carrier.phone,
        '身份证号': carrier.id_card,
        '备注': carrier.remark,
        '状态': carrier.status,
        '车辆数量': (carrier.vehicles || []).length
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '承运单位');
      XLSX.writeFile(wb, `承运单位_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      messageApi.success({
        content: 'Excel文件导出成功！',
        duration: 3,
      });
    } catch (error) {
      messageApi.error({
        content: 'Excel文件导出失败，请重试',
        duration: 4,
      });
    } finally {
      setExportLoading(false);
    }
  }, [messageApi]);

  // 表格列定义
  const columns = useMemo(() => [
    {
      title: '承运单位',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: '负责人',
      dataIndex: 'manager',
      key: 'manager',
      render: (text: string) => (
        <Space>
          <UserOutlined />
          {text}
        </Space>
      )
    },
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
      render: (text: string) => (
        <Space>
          <EnvironmentOutlined />
          {text}
        </Space>
      )
    },
    {
      title: '电话',
      dataIndex: 'phone',
      key: 'phone',
      render: (text: string) => (
        <Space>
          <PhoneOutlined />
          {text}
        </Space>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === '开启' ? 'green' : 'red'}>
          {status}
        </Tag>
      )
    },
    {
      title: '车辆数量',
      dataIndex: 'vehicles',
      key: 'vehicleCount',
      render: (vehicles: Vehicle[]) => (
        <Badge count={vehicles?.length || 0} showZero>
          <CarOutlined style={{ fontSize: 16 }} />
        </Badge>
      )
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: isMobile ? 120 : 150,
      render: (_: any, record: Carrier) => (
        <Space size="small" direction={isMobile ? 'vertical' : 'horizontal'}>
          <Tooltip title="查看详情">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              size={isMobile ? 'small' : 'middle'}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              size={isMobile ? 'small' : 'middle'}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个承运单位吗？"
            onConfirm={() => handleDelete(record.id!)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />} 
                size={isMobile ? 'small' : 'middle'}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ], [isMobile, handleView, handleEdit, handleDelete]);

  return (
    <>
      {contextHolder}
      <div style={{ padding: isMobile ? '16px' : '12px' }}>
        <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} sm={24} md={8}>
              <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>承运单位管理</Title>
            </Col>
            <Col xs={24} sm={24} md={16}>
              <Space 
                direction={isMobile ? 'vertical' : 'horizontal'} 
                style={{ width: '100%', justifyContent: isMobile ? 'stretch' : 'flex-end' }}
                size="small"
              >
                <Input
                  placeholder="搜索承运单位名称、负责人或电话"
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchText(value);
                    // 输入即搜索，延迟300ms避免频繁请求
                    if (searchTimeout.current) {
                      clearTimeout(searchTimeout.current);
                    }
                    searchTimeout.current = setTimeout(() => {
                      handleSearch(value);
                    }, 300);
                  }}
                  allowClear
                  style={{ width: isMobile ? '100%' : 250 }}
                />
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={() => fetchData()}
                  style={{ width: isMobile ? '100%' : 'auto' }}
                >
                  刷新
                </Button>
                <Button 
                  icon={<UploadOutlined />} 
                  onClick={() => setImportModalVisible(true)}
                  style={{ width: isMobile ? '100%' : 'auto' }}
                >
                  批量导入
                </Button>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'csv',
                        label: '导出CSV',
                        onClick: exportToCSV,
                        icon: <DownloadOutlined />
                      },
                      {
                        key: 'excel',
                        label: '导出Excel',
                        onClick: exportToExcel,
                        icon: <DownloadOutlined />
                      }
                    ]
                  }}
                >
                  <Button 
                    icon={<DownloadOutlined />} 
                    loading={exportLoading}
                    style={{ width: isMobile ? '100%' : 'auto' }}
                  >
                    导出
                  </Button>
                </Dropdown>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={handleAdd}
                  style={{ width: isMobile ? '100%' : 'auto' }}
                >
                  新增承运单位
                </Button>
              </Space>
            </Col>
          </Row>
          {isMobile && (
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                共 {total} 个承运单位（当前页显示 {data.length} 个）
              </Text>
            </div>
          )}
        </div>

        {isMobile ? (
          // 移动端卡片布局
          <div>
            {data.map(renderCarrierCard)}
            {data.length === 0 && !loading && (
              <Card style={{ textAlign: 'center', padding: '40px' }}>
                <Text type="secondary">暂无承运单位数据</Text>
              </Card>
            )}
            
            {/* 移动端分页 */}
            {total > pageSize && (
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <Pagination
                  current={current}
                  pageSize={pageSize}
                  total={total}
                  showSizeChanger={false}
                  showQuickJumper={true}
                  showTotal={(total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
                  onChange={handlePageChange}
                  size="small"
                />
              </div>
            )}
          </div>
        ) : (
          // 桌面端表格布局
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1000 }}
            pagination={{
              current: current,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              onChange: handlePageChange,
              onShowSizeChange: handlePageChange
            }}
            size="middle"
          />
        )}
      </Card>
    </div>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingCarrier ? '编辑承运单位' : '新增承运单位'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={isMobile ? '98%' : 800}
        style={{ top: isMobile ? 20 : 100 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            status: '开启',
            vehicles: []
          }}
        >
          <Form.Item
            name="name"
            label="承运单位名称"
            rules={[{ required: true, message: '请输入承运单位名称' }]}
          >
            <Input placeholder="请输入承运单位名称" />
          </Form.Item>
          
          <Form.Item
            name="manager"
            label="负责人"
          >
            <Input placeholder="请输入负责人姓名" />
          </Form.Item>
          
          <Form.Item
            name="phone"
            label="联系电话"
          >
            <Input placeholder="请输入联系电话" />
          </Form.Item>
          
          <Form.Item
            name="id_card"
            label="身份证号"
          >
            <Input placeholder="请输入身份证号" />
          </Form.Item>
          
          <Form.Item
            name="status"
            label="状态"
          >
            <Select>
              <Option value="开启">开启</Option>
              <Option value="关闭">关闭</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="address"
            label="地址"
          >
            <Input placeholder="请输入地址" />
          </Form.Item>
          
          <Form.Item
            name="remark"
            label="备注"
          >
            <Input.TextArea rows={3} placeholder="请输入备注信息" />
          </Form.Item>
          
          <Form.Item label="车辆信息">
            <Form.List name="vehicles">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card key={key} size="small" style={{ marginBottom: 8 }}>
                      <Row gutter={16}>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'plate_number']}
                            rules={[{ required: true, message: '请输入车牌号' }]}
                          >
                            <Input placeholder="车牌号" />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'driver']}
                          >
                            <Input placeholder="司机姓名" />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'phone']}
                          >
                            <Input placeholder="联系电话" />
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item
                            {...restField}
                            name={[name, 'remark']}
                          >
                            <Input placeholder="备注" />
                          </Form.Item>
                        </Col>
                        <Col span={1}>
                          <Button 
                            type="text" 
                            danger 
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                          />
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    添加车辆
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>
          
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setModalVisible(false);
                setEditingCarrier(null);
                form.resetFields();
              }}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingCarrier ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 查看详情抽屉 */}
      <Drawer
        title="承运单位详情"
        placement={isMobile ? 'bottom' : 'right'}
        width={isMobile ? '100%' : 600}
        height={isMobile ? '85%' : undefined}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        {selectedCarrier && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="承运单位名称">
              {selectedCarrier.name}
            </Descriptions.Item>
            <Descriptions.Item label="负责人">
              {selectedCarrier.manager}
            </Descriptions.Item>
            <Descriptions.Item label="地址">
              {selectedCarrier.address}
            </Descriptions.Item>
            <Descriptions.Item label="电话">
              {selectedCarrier.phone}
            </Descriptions.Item>
            <Descriptions.Item label="身份证号">
              {selectedCarrier.id_card}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={selectedCarrier.status === '开启' ? 'green' : 'red'}>
                {selectedCarrier.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="备注">
              {selectedCarrier.remark}
            </Descriptions.Item>
            <Descriptions.Item label="车辆数量">
              <Badge count={selectedCarrier.vehicles?.length || 0} showZero>
                <CarOutlined style={{ fontSize: 16 }} />
              </Badge>
            </Descriptions.Item>
          </Descriptions>
        )}
        
        {selectedCarrier?.vehicles && selectedCarrier.vehicles.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <Divider orientation="left">车辆信息</Divider>
            {selectedCarrier.vehicles.map((vehicle, index) => renderVehicleCard(vehicle, index))}
          </div>
        )}
      </Drawer>

      {/* 批量导入模态框 */}
      <Modal
        title="批量导入承运单位"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        width={800}
      >
        {!importResult ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <Text>请确认字段映射关系：</Text>
            </div>
            
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {Object.keys(fieldMapping).map(sourceField => (
                <div key={sourceField} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: 8,
                  padding: '8px 0',
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <div style={{ width: 120, textAlign: 'right', marginRight: 16 }}>
                    <Text strong>{sourceField}</Text>
                  </div>
                  <div style={{ margin: '0 16px' }}>
                    <Text type="secondary">→</Text>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Select
                      value={fieldMapping[sourceField]}
                      onChange={(value) => setFieldMapping(prev => ({ ...prev, [sourceField]: value }))}
                      style={{ width: '100%' }}
                      placeholder="选择目标字段"
                    >
                      <Option value="">不导入</Option>
                      <Option value="name">承运单位名称</Option>
                      <Option value="manager">负责人</Option>
                      <Option value="address">地址</Option>
                      <Option value="phone">电话</Option>
                      <Option value="id_card">身份证号</Option>
                      <Option value="remark">备注</Option>
                      <Option value="status">状态</Option>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
            
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setImportModalVisible(false)}>
                  取消
                </Button>
                <Button type="primary" onClick={handleImport}>
                  开始导入
                </Button>
              </Space>
            </div>
          </>
        ) : (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>导入结果：</Text>
            </div>
            
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, color: '#52c41a', fontWeight: 'bold' }}>
                      {importResult.success}
                    </div>
                    <div>成功导入</div>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, color: '#faad14', fontWeight: 'bold' }}>
                      {importResult.skipped}
                    </div>
                    <div>跳过</div>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, color: '#ff4d4f', fontWeight: 'bold' }}>
                      {importResult.failed}
                    </div>
                    <div>失败</div>
                  </div>
                </Card>
              </Col>
            </Row>
            
            {importResult.errors && importResult.errors.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>错误详情：</Text>
                <div style={{ 
                  maxHeight: 200, 
                  overflowY: 'auto', 
                  border: '1px solid #f0f0f0',
                  padding: 8,
                  marginTop: 8
                }}>
                  {importResult.errors.map((error: any, index: number) => (
                    <div key={index} style={{ marginBottom: 4 }}>
                      <Text type="danger">第{error.row}行：{error.reason}</Text>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={() => {
                  setImportModalVisible(false);
                  setImportData([]);
                  setFieldMapping({});
                  setImportResult(null);
                }}>
                  完成并关闭
                </Button>
                <Button onClick={() => {
                  setImportResult(null);
                }}>
                  重新导入
                </Button>
              </Space>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default CarrierManagement;
